import { useCallback, useEffect, useMemo, useState } from 'react';
import { podeFazer } from '../../acesso';
import type { Eu } from '../../acesso';
import type { Sessao } from '../../auth';
import { Icone } from '../../design/Icone';
import {
  ROTULO_DA_BASE,
  ROTULO_DA_CATEGORIA,
  lerCatalogo,
  lerHistorico,
  lerMetas,
  mudarStatus,
  quantosNiveis,
  reais,
  salvarConfig,
} from '../../metas';
import type {
  ConfigDasMetas,
  IndicadorDoCatalogo,
  MetaGravada,
  RegraDaMeta,
  VersaoDaMeta,
} from '../../metas';
import { ErroDaLista, Esqueleto, useEscape } from '../equipe/Equipe';
import { EditorDeMeta } from './EditorDeMeta';
import { Fechamento } from './Fechamento';
import './metas.css';

/**
 * CONFIGURAÇÕES → METAS & PREMIAÇÃO (dono, 29/09 — fase 2). O Carlos decide sozinho, sem mexer em
 * código: "Ticket vai pagar? Quanto? Qual %? Quantos níveis? Cupons? Não."
 *
 * INDICADOR ≠ META ≠ PREMIAÇÃO: o catálogo lista as métricas; uma meta escolhe uma delas; só a
 * meta com premiação LIGADA paga. Toda conta (régua, prêmio, simulação) é do servidor.
 */

type Aba = 'metas' | 'mapa' | 'config' | 'fechamento';

const dataCurta = (dia: string | null) =>
  dia ? `${dia.slice(8, 10)}/${dia.slice(5, 7)}/${dia.slice(0, 4)}` : '—';
const ROTULO_DO_STATUS: Record<MetaGravada['status'], string> = {
  RASCUNHO: 'Rascunho',
  ATIVA: 'Ativa',
  PAUSADA: 'Pausada',
  ENCERRADA: 'Encerrada',
  EXCLUIDA: 'Excluída',
};

function resumoDaRegua(r: RegraDaMeta): string {
  const n = r.niveis;
  const sinal = r.direcao === 'MENOR_MELHOR' ? '−' : '+';
  if (n.tipo === 'MANUAL') return 'valores manuais';
  if (n.tipo === 'VALOR_FIXO') return `${sinal}${n.passo} por nível`;
  const iguais = n.passosPct.every((p) => p === n.passosPct[0]);
  const txt = iguais
    ? `${sinal}${String(n.passosPct[0]).replace('.', ',')}%`
    : 'percentuais por nível';
  return n.tipo === 'COMPOSTO' ? `${txt} composto` : `${txt} sobre a base`;
}

export function MetasEPremiacao({ sessao, eu }: { sessao: Sessao; eu: Eu | null }) {
  const [aba, setAba] = useState<Aba>('metas');
  const [dados, setDados] = useState<{ metas: MetaGravada[]; config: ConfigDasMetas } | null>(null);
  const [catalogo, setCatalogo] = useState<IndicadorDoCatalogo[] | null>(null);
  const [erro, setErro] = useState(false);
  const [editando, setEditando] = useState<{ meta: MetaGravada | null; duplicar?: boolean } | null>(
    null,
  );
  const [historico, setHistorico] = useState<MetaGravada | null>(null);
  const [status, setStatus] = useState<{
    meta: MetaGravada;
    para: 'ATIVA' | 'PAUSADA' | 'EXCLUIDA';
  } | null>(null);
  const podeGerenciar = podeFazer(eu, 'metas.gerenciar');
  const podeDinheiro = podeFazer(eu, 'metas.gerenciar_financeiro');

  const carregar = useCallback(async () => {
    setErro(false);
    const [m, c] = await Promise.all([lerMetas(), lerCatalogo()]);
    if (m.ok) setDados(m.dados);
    if (c.ok) setCatalogo(c.dados);
    if (!m.ok || !c.ok) setErro(true);
  }, []);
  useEffect(() => {
    void carregar();
  }, [carregar]);

  const porCodigo = useMemo(() => new Map((catalogo ?? []).map((i) => [i.codigo, i])), [catalogo]);
  const ativas = dados?.metas.filter((m) => m.status === 'ATIVA') ?? [];
  const pagas = ativas.filter((m) => m.premioMaximo !== null);

  return (
    <div className="ac ac-cheia mp">
      <div className="ac-cabeca">
        <div>
          <h1 className="ac-titulo">Metas & Premiação</h1>
          <div className="ac-subtitulo">
            Indicador não é meta, e meta não é premiação: nenhum número paga sozinho — só a meta com
            premiação ligada.
          </div>
        </div>
        <div className="ac-acoes">
          {podeGerenciar && (
            <button
              type="button"
              className="ac-botao ac-botao-primario"
              onClick={() => setEditando({ meta: null })}
              disabled={!catalogo}
            >
              <Icone nome="plus" tamanho={14} traco={2} /> Nova meta
            </button>
          )}
        </div>
      </div>

      <div className="ac-abas mp-abas" role="tablist">
        {(
          [
            ['metas', 'Metas'],
            ['mapa', 'Mapa de premiação'],
            ['config', 'Teto e pagamento'],
            ['fechamento', 'Fechamento do mês'],
          ] as const
        ).map(([k, rotulo]) => (
          <button
            key={k}
            type="button"
            role="tab"
            aria-selected={aba === k}
            className={`ac-aba${aba === k ? ' ac-aba-ativa' : ''}`}
            onClick={() => setAba(k)}
          >
            {rotulo}
          </button>
        ))}
      </div>

      {erro && (
        <ErroDaLista
          titulo="Não foi possível carregar as metas"
          texto="Nada foi alterado. Tente de novo."
          aoTentar={() => void carregar()}
        />
      )}
      {!erro && (!dados || !catalogo) && <Esqueleto linhas={5} />}

      {dados && catalogo && aba === 'metas' && (
        <>
          <div className="mp-resumo">
            <Numero rotulo="Metas ativas" valor={String(ativas.length)} />
            <Numero rotulo="Com premiação" valor={String(pagas.length)} destaque />
            <Numero rotulo="Só acompanhamento" valor={String(ativas.length - pagas.length)} />
            <Numero
              rotulo="Teto mensal por pessoa"
              valor={dados.config.tetoGeral === null ? 'sem teto' : reais(dados.config.tetoGeral)}
            />
          </div>
          <ListaDeMetas
            metas={dados.metas}
            porCodigo={porCodigo}
            podeGerenciar={podeGerenciar}
            aoEditar={(m) => setEditando({ meta: m })}
            aoDuplicar={(m) => setEditando({ meta: m, duplicar: true })}
            aoHistorico={setHistorico}
            aoStatus={(meta, para) => setStatus({ meta, para })}
            aoCriar={() => setEditando({ meta: null })}
          />
        </>
      )}
      {dados && catalogo && aba === 'mapa' && (
        <MapaDePremiacao metas={dados.metas} catalogo={catalogo} />
      )}
      {dados && aba === 'config' && (
        <ConfigDoBonus
          sessao={sessao}
          config={dados.config}
          podeMudar={podeDinheiro}
          aoSalvar={() => void carregar()}
        />
      )}

      {aba === 'fechamento' && (
        <Fechamento
          sessao={sessao}
          podeFechar={podeFazer(eu, 'metas.fechar')}
          podePagar={podeFazer(eu, 'metas.pagar')}
        />
      )}

      {editando && catalogo && (
        <EditorDeMeta
          sessao={sessao}
          catalogo={catalogo}
          meta={editando.meta}
          duplicar={editando.duplicar ?? false}
          podeDinheiro={podeDinheiro}
          aoFechar={() => setEditando(null)}
          aoSalvar={() => {
            setEditando(null);
            void carregar();
          }}
        />
      )}
      {historico && (
        <HistoricoDaMeta
          meta={historico}
          porCodigo={porCodigo}
          aoFechar={() => setHistorico(null)}
        />
      )}
      {status && (
        <MudarStatus
          sessao={sessao}
          meta={status.meta}
          para={status.para}
          aoFechar={() => setStatus(null)}
          aoFeito={() => {
            setStatus(null);
            void carregar();
          }}
        />
      )}
    </div>
  );
}

function Numero({
  rotulo,
  valor,
  destaque = false,
}: {
  rotulo: string;
  valor: string;
  destaque?: boolean;
}) {
  return (
    <div className={`mp-numero${destaque ? ' mp-numero-destaque' : ''}`}>
      <span>{rotulo}</span>
      <b>{valor}</b>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Lista
 * ------------------------------------------------------------------ */

function ListaDeMetas({
  metas,
  porCodigo,
  podeGerenciar,
  aoEditar,
  aoDuplicar,
  aoHistorico,
  aoStatus,
  aoCriar,
}: {
  metas: MetaGravada[];
  porCodigo: Map<string, IndicadorDoCatalogo>;
  podeGerenciar: boolean;
  aoEditar: (m: MetaGravada) => void;
  aoDuplicar: (m: MetaGravada) => void;
  aoHistorico: (m: MetaGravada) => void;
  aoStatus: (m: MetaGravada, para: 'ATIVA' | 'PAUSADA' | 'EXCLUIDA') => void;
  aoCriar: () => void;
}) {
  if (!metas.length)
    return (
      <div className="ac-cartao mp-vazio">
        <Icone nome="gauge" tamanho={26} traco={1.5} />
        <b>Nenhuma meta ainda</b>
        <p>
          Comece por uma meta de acompanhamento (sem dinheiro) ou já com premiação — por exemplo,
          "Melhoria do Ticket Médio", 5 níveis de +5%, R$ 10 a R$ 50.
        </p>
        {podeGerenciar && (
          <button type="button" className="ac-botao ac-botao-primario" onClick={aoCriar}>
            <Icone nome="plus" tamanho={14} traco={2} /> Criar a primeira meta
          </button>
        )}
      </div>
    );
  return (
    <div className="ac-cartao ac-tabela-rolo">
      <table className="ac-tabela mp-tabela" data-sem-ordenar>
        <thead>
          <tr>
            <th>Meta</th>
            <th>Indicador</th>
            <th>Régua</th>
            <th>Referência</th>
            <th className="ac-num">Bônus máx.</th>
            <th>Período</th>
            <th>Público</th>
            <th>Status</th>
            <th aria-label="Ações" />
          </tr>
        </thead>
        <tbody>
          {metas.map((m) => {
            const r = m.regra ?? m.agendada?.regra ?? null;
            const ind = porCodigo.get(m.indicador);
            return (
              <tr key={m.id}>
                <td>
                  <div className="mp-meta-nome">
                    <b>{m.nome}</b>
                    <span>
                      {m.tipo === 'CAMPANHA' ? 'Campanha' : 'Permanente'} · v{m.versao}
                      {m.atualizadoPor ? ` · ${m.atualizadoPor}` : ''}
                    </span>
                  </div>
                </td>
                <td>
                  <div className="mp-meta-nome">
                    <span className="mp-ind">{ind?.rotulo ?? m.indicador}</span>
                    <span>
                      {r?.direcao === 'MENOR_MELHOR' ? '↓ menor é melhor' : '↑ maior é melhor'}
                    </span>
                  </div>
                </td>
                <td>
                  {r ? `${String(quantosNiveis(r.niveis))} níveis · ${resumoDaRegua(r)}` : '—'}
                </td>
                <td>{r ? ROTULO_DA_BASE(r.base) : '—'}</td>
                <td className="ac-num">
                  {m.premioMaximo === null ? (
                    <span className="mp-sem-premio">acompanhamento</span>
                  ) : (
                    <b className="mp-valor">{reais(m.premioMaximo)}</b>
                  )}
                </td>
                <td>
                  {r
                    ? r.fim
                      ? `${dataCurta(r.inicio)} a ${dataCurta(r.fim)}`
                      : `desde ${dataCurta(r.inicio)}`
                    : '—'}
                </td>
                <td>
                  {r?.publico.tipo === 'CONTAS'
                    ? `${String(r.publico.contas.length)} pessoa(s)`
                    : 'Todos'}
                  {r && r.publico.excecoes.length > 0
                    ? ` · ${String(r.publico.excecoes.length)} fora`
                    : ''}
                </td>
                <td>
                  <span className={`mp-status mp-status-${m.status.toLowerCase()}`}>
                    {ROTULO_DO_STATUS[m.status]}
                  </span>
                  {m.agendada && (
                    <span
                      className="mp-agendada"
                      title="Mudança salva que vale a partir desta data"
                    >
                      muda em {dataCurta(m.agendada.vigenteDesde)}
                    </span>
                  )}
                </td>
                <td className="mp-acoes">
                  <button type="button" className="ac-link" onClick={() => aoHistorico(m)}>
                    Histórico
                  </button>
                  {podeGerenciar && (
                    <>
                      <button type="button" className="ac-link" onClick={() => aoEditar(m)}>
                        Editar
                      </button>
                      <button type="button" className="ac-link" onClick={() => aoDuplicar(m)}>
                        Duplicar
                      </button>
                      {m.status === 'ATIVA' ? (
                        <button
                          type="button"
                          className="ac-link"
                          onClick={() => aoStatus(m, 'PAUSADA')}
                        >
                          Pausar
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="ac-link"
                          onClick={() => aoStatus(m, 'ATIVA')}
                        >
                          Ativar
                        </button>
                      )}
                      <button
                        type="button"
                        className="ac-link mp-perigo"
                        onClick={() => aoStatus(m, 'EXCLUIDA')}
                      >
                        Excluir
                      </button>
                    </>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Mapa de premiação
 * ------------------------------------------------------------------ */

function MapaDePremiacao({
  metas,
  catalogo,
}: {
  metas: MetaGravada[];
  catalogo: IndicadorDoCatalogo[];
}) {
  const ativas = metas.filter((m) => m.status === 'ATIVA');
  const categorias = [...new Set(catalogo.map((i) => i.categoria))];
  return (
    <div className="mp-mapa">
      <p className="ac-nota">
        O que paga hoje, indicador por indicador. "Sem meta" é só um número no Meu Desempenho — não
        paga nada.
      </p>
      {categorias.map((cat) => (
        <section key={cat} className="ac-cartao mp-mapa-grupo">
          <h2 className="mp-mapa-titulo">{ROTULO_DA_CATEGORIA[cat]}</h2>
          <ul>
            {catalogo
              .filter((i) => i.categoria === cat)
              .map((i) => {
                const dela = ativas.filter((m) => m.indicador === i.codigo);
                const paga = dela.filter((m) => m.premioMaximo !== null);
                return (
                  <li key={i.codigo} className="mp-mapa-linha">
                    <span className="mp-mapa-ind">{i.rotulo}</span>
                    {paga.length > 0 ? (
                      <span className="mp-mapa-estado">
                        <span className="mp-pilula mp-pilula-paga">PAGA</span>
                        {paga.map((m) => {
                          const r = m.regra;
                          return (
                            <span key={m.id} className="mp-mapa-detalhe">
                              {m.nome} ·{' '}
                              {r
                                ? `${String(quantosNiveis(r.niveis))} níveis · ${resumoDaRegua(r)}`
                                : ''}{' '}
                              · até <b>{reais(m.premioMaximo)}</b>
                            </span>
                          );
                        })}
                      </span>
                    ) : dela.length > 0 ? (
                      <span className="mp-mapa-estado">
                        <span className="mp-pilula">SEM PREMIAÇÃO</span>
                        <span className="mp-mapa-detalhe">meta de acompanhamento</span>
                      </span>
                    ) : (
                      <span className="mp-mapa-estado">
                        <span className="mp-pilula mp-pilula-apagada">SEM META</span>
                      </span>
                    )}
                  </li>
                );
              })}
          </ul>
        </section>
      ))}
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Teto e pagamento
 * ------------------------------------------------------------------ */

function ConfigDoBonus({
  sessao,
  config,
  podeMudar,
  aoSalvar,
}: {
  sessao: Sessao;
  config: ConfigDasMetas;
  podeMudar: boolean;
  aoSalvar: () => void;
}) {
  const [comTeto, setComTeto] = useState(config.tetoGeral !== null);
  const [teto, setTeto] = useState(config.tetoGeral ?? '300');
  const [dia, setDia] = useState(String(config.diaDePagamento));
  const [intervalo, setIntervalo] = useState(String(config.intervaloDeAlertasHoras));
  const [motivo, setMotivo] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; texto: string } | null>(null);
  const salvar = () => {
    setEnviando(true);
    setMsg(null);
    void salvarConfig(
      sessao,
      {
        tetoGeral: comTeto ? teto.replace(',', '.') : null,
        diaDePagamento: Number(dia),
        intervaloDeAlertasHoras: Number(intervalo),
      },
      config.versao,
      motivo,
    ).then((r) => {
      setEnviando(false);
      if (r.tipo === 'confirmada') {
        setMsg({ ok: true, texto: 'Salvo. Vale para o bônus projetado a partir de agora.' });
        aoSalvar();
      } else
        setMsg({
          ok: false,
          texto:
            r.tipo === 'recusada'
              ? r.mensagem
              : 'Sem resposta do servidor — confira se salvou antes de repetir.',
        });
    });
  };
  return (
    <section className="ac-cartao mp-config">
      <div className="mp-config-linha">
        <label className="mp-interruptor">
          <input
            type="checkbox"
            checked={comTeto}
            disabled={!podeMudar}
            onChange={(e) => setComTeto(e.target.checked)}
          />
          <span>Aplicar teto mensal por balconista</span>
        </label>
        {comTeto && (
          <label className="mp-campo mp-campo-curto">
            <span>Teto (R$)</span>
            <input
              inputMode="decimal"
              value={teto}
              disabled={!podeMudar}
              onChange={(e) => setTeto(e.target.value)}
            />
          </label>
        )}
        <p className="ac-nota">
          Se as metas somarem mais que o teto, o bônus projetado para no teto — e o excedente
          aparece à vista, nunca escondido.
        </p>
      </div>
      <div className="mp-config-linha mp-config-dupla">
        <label className="mp-campo mp-campo-curto">
          <span>Dia do pagamento (mês seguinte)</span>
          <input
            inputMode="numeric"
            value={dia}
            disabled={!podeMudar}
            onChange={(e) => setDia(e.target.value)}
          />
        </label>
        <label className="mp-campo mp-campo-curto">
          <span>Intervalo mínimo entre alertas iguais (horas)</span>
          <input
            inputMode="numeric"
            value={intervalo}
            disabled={!podeMudar}
            onChange={(e) => setIntervalo(e.target.value)}
          />
        </label>
      </div>
      {podeMudar ? (
        <div className="mp-config-linha">
          <label className="mp-campo">
            <span>Por que está mudando?</span>
            <input
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              placeholder="ex.: teto de outubro combinado com o Carlos"
            />
          </label>
          <button
            type="button"
            className="ac-botao ac-botao-primario"
            disabled={enviando || motivo.trim().length < 5}
            onClick={salvar}
          >
            {enviando ? 'Salvando…' : 'Salvar'}
          </button>
          {msg && (
            <p className={msg.ok ? 'mp-ok' : 'mp-falha'} role={msg.ok ? 'status' : 'alert'}>
              {msg.texto}
            </p>
          )}
        </div>
      ) : (
        <p className="ac-nota">
          Só quem cuida do dinheiro das metas (o dono) muda o teto e o pagamento.
        </p>
      )}
    </section>
  );
}

/* ------------------------------------------------------------------ *
 * Status e histórico
 * ------------------------------------------------------------------ */

function MudarStatus({
  sessao,
  meta,
  para,
  aoFechar,
  aoFeito,
}: {
  sessao: Sessao;
  meta: MetaGravada;
  para: 'ATIVA' | 'PAUSADA' | 'EXCLUIDA';
  aoFechar: () => void;
  aoFeito: () => void;
}) {
  useEscape(aoFechar, true);
  const [motivo, setMotivo] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [falha, setFalha] = useState<string | null>(null);
  const verbo = { ATIVA: 'Ativar', PAUSADA: 'Pausar', EXCLUIDA: 'Excluir' }[para];
  const texto = {
    ATIVA: 'A meta passa a valer no Meu Desempenho (e, se tiver premiação, no bônus projetado).',
    PAUSADA: 'A meta sai do bônus projetado enquanto estiver pausada. O histórico fica.',
    EXCLUIDA: 'A meta some da lista e não volta. O histórico e a trilha de auditoria ficam.',
  }[para];
  return (
    <div
      className="ac-veu ac-veu-centro"
      onMouseDown={(e) => e.target === e.currentTarget && aoFechar()}
    >
      <div
        className="ac-cartao mp-modal"
        role="dialog"
        aria-modal="true"
        aria-label={`${verbo} meta`}
      >
        <h2>
          {verbo} "{meta.nome}"?
        </h2>
        <p>{texto}</p>
        <label className="mp-campo">
          <span>Por quê?</span>
          <input autoFocus value={motivo} onChange={(e) => setMotivo(e.target.value)} />
        </label>
        {falha && (
          <p className="mp-falha" role="alert">
            {falha}
          </p>
        )}
        <div className="mp-modal-acoes">
          <button type="button" className="ac-botao" onClick={aoFechar}>
            Cancelar
          </button>
          <button
            type="button"
            className={`ac-botao ${para === 'EXCLUIDA' ? 'ac-botao-perigo' : 'ac-botao-primario'}`}
            disabled={enviando || motivo.trim().length < 5}
            onClick={() => {
              setEnviando(true);
              setFalha(null);
              void mudarStatus(sessao, meta.id, para, motivo).then((r) => {
                setEnviando(false);
                if (r.tipo === 'confirmada') aoFeito();
                else
                  setFalha(
                    r.tipo === 'recusada'
                      ? r.mensagem
                      : 'Sem resposta do servidor — confira antes de repetir.',
                  );
              });
            }}
          >
            {enviando ? 'Salvando…' : verbo}
          </button>
        </div>
      </div>
    </div>
  );
}

function HistoricoDaMeta({
  meta,
  porCodigo,
  aoFechar,
}: {
  meta: MetaGravada;
  porCodigo: Map<string, IndicadorDoCatalogo>;
  aoFechar: () => void;
}) {
  useEscape(aoFechar, true);
  const [v, setV] = useState<VersaoDaMeta[] | null>(null);
  const [erro, setErro] = useState(false);
  useEffect(() => {
    void lerHistorico(meta.id).then((r) => (r.ok ? setV(r.dados) : setErro(true)));
  }, [meta.id]);
  return (
    <div className="ac-veu" onMouseDown={(e) => e.target === e.currentTarget && aoFechar()}>
      <aside
        className="ac-drawer mp-drawer"
        role="dialog"
        aria-modal="true"
        aria-label="Histórico da meta"
      >
        <div className="ac-drawer-cabeca">
          <div>
            <div className="ac-drawer-nome">Histórico · {meta.nome}</div>
            <div className="ac-drawer-meta">
              {porCodigo.get(meta.indicador)?.rotulo ?? meta.indicador}
            </div>
          </div>
          <button type="button" className="ac-fechar" onClick={aoFechar} aria-label="Fechar">
            <Icone nome="x" tamanho={15} traco={1.7} />
          </button>
        </div>
        <div className="mp-historico">
          {erro && <p className="mp-falha">Não foi possível carregar o histórico.</p>}
          {!erro && !v && <Esqueleto linhas={4} />}
          {v?.map((x) => (
            <div key={x.versao} className="mp-versao">
              <div className="mp-versao-topo">
                <b>v{x.versao}</b>
                <span>
                  {new Date(x.em).toLocaleString('pt-BR', {
                    timeZone: 'America/Sao_Paulo',
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                  {x.autor ? ` · ${x.autor}` : ''}
                </span>
                <span className="mp-agendada">vale desde {dataCurta(x.vigenteDesde)}</span>
              </div>
              <p className="mp-versao-motivo">"{x.motivo}"</p>
              <p className="mp-versao-regra">
                {String(quantosNiveis(x.regra.niveis))} níveis · {resumoDaRegua(x.regra)} ·{' '}
                {ROTULO_DA_BASE(x.regra.base)} ·{' '}
                {x.regra.premiacao.ativa
                  ? x.regra.premiacao.premio.tipo === 'POR_NIVEL'
                    ? `R$ ${x.regra.premiacao.premio.valores.join(' / ')}${x.regra.premiacao.acumulo === 'SOMA' ? ' (soma)' : ''}`
                    : x.regra.premiacao.premio.tipo === 'UNICO'
                      ? `prêmio único R$ ${x.regra.premiacao.premio.valor}`
                      : ''
                  : 'sem premiação'}
                {x.regra.premiacao.ativa && x.regra.premiacao.tetoDaMeta
                  ? ` · teto R$ ${x.regra.premiacao.tetoDaMeta}`
                  : ''}
              </p>
            </div>
          ))}
        </div>
      </aside>
    </div>
  );
}
